import { apiFetch } from './client';

/**
 * FASE 4 (El Edificio) — el menú del inquilino activo.
 * Prefijo: /api/modulos
 *
 * **Esta es la única fuente del menú.** El sidebar y el buscador de comandos
 * comen los dos de aquí. Hasta hoy el sidebar tenía una lista de 19 items
 * cableada a mano en el componente: eso hacía que la Fase 3 no sirviera para
 * nada — el servidor decidía qué módulos tiene cada inquilino y el panel le
 * mostraba los mismos links a todo el mundo.
 *
 * Quién decide qué: **el servidor**. Aquí no se filtra por rol ni por módulo.
 * Filtrar en el navegador sería esconder un botón, y esconder un botón no es
 * seguridad — la API responde 403/404 igual, y esta lista solo dice qué tiene
 * sentido dibujar.
 */

/** Una entrada del menú, ya resuelta por el servidor. */
export interface ItemDeMenu {
  ruta: string;
  etiqueta: string;
  /** Nombre del icono de lucide. El panel resuelve el componente. */
  icono: string;
  moduloId: string;
}

export interface GrupoDeMenu {
  /** `atencion` | `crecimiento` | `operacion` | `configuracion` */
  grupo: string;
  items: ItemDeMenu[];
}

/** Una ruta activa. Incluye las que NO se dibujan en el menú (detalles). */
export interface RutaActiva extends ItemDeMenu {
  enMenu: boolean;
  grupo: string;
}

export interface MenuResponse {
  /** Nombre del negocio activo, para el encabezado. */
  negocio: string | null;
  menu: GrupoDeMenu[];
  /** TODAS las rutas que existen para este inquilino y este rol. */
  rutas: RutaActiva[];
  /** «Ver como»: la administración del sistema puede entrar a las otras cuentas. */
  verComo?: boolean;
}

export function getMenu(): Promise<MenuResponse> {
  return apiFetch<MenuResponse>('/api/modulos/menu');
}

// ─── Administración de módulos (PASO 5 del alta de cliente) ────────────────

export interface ModuloDelCatalogo {
  id: string;
  nombre: string;
  descripcion: string;
  icono: string;
  grupo: string;
  dependeDe: string[];
  /** `false` = declarado pero NO construido. Nunca se finge lo contrario. */
  construido: boolean;
}

export interface CatalogoDeModulos {
  modulos: ModuloDelCatalogo[];
  /** Los módulos que el negocio tiene encendidos de verdad (lo guardado, con dependencias y fijos). */
  activos: string[];
  tipos: Array<{ tipo: string; modulos: string[] }>;
}

export function getCatalogo(): Promise<CatalogoDeModulos> {
  return apiFetch<CatalogoDeModulos>('/api/modulos');
}

/** Enciende/apaga. El servidor cierra las dependencias y dice cuáles agregó. */
export function setModulos(
  modulos: string[],
): Promise<{ modulos: string[]; agregadosPorDependencia: string[] }> {
  return apiFetch('/api/modulos', { method: 'PUT', body: { modulos } });
}

/** Aplica el paquete de un tipo de negocio. REEMPLAZA, no suma. */
export function aplicarTipo(tipo: string): Promise<{ tipo: string; modulos: string[] }> {
  return apiFetch('/api/modulos/tipo', { method: 'PUT', body: { tipo } });
}
